import { App } from 'antd';
import { sendInvoice } from 'services/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';

type MessageApi = ReturnType<typeof App.useApp>['message'];
type ModalApi = ReturnType<typeof App.useApp>['modal'];

interface ConfirmSendOptions {
  modal: ModalApi;
  message: MessageApi;
  invoiceNumber?: string | null;
  email?: string | null;
}

/** POST /invoicing/:ref/send and toast the result. */
const sendAndToast = async (
  ref: string,
  message: MessageApi
): Promise<boolean> => {
  try {
    const { sent, email } = await sendInvoice(ref);

    if (sent) {
      message.success(email ? `Sent to ${email}` : 'Invoice sent');
    } else {
      message.warning(
        email ? `Could not send to ${email}` : 'No email on file'
      );
    }

    return sent;
  } catch (err) {
    showErrorMessage(err);

    return false;
  }
};

/**
 * Ask first, then send. Sending emails the patient, so it is never a single
 * click — the confirm names the recipient so the admin can check it before the
 * mail goes out. Shared by the invoice list row and the detail page; `ref` is
 * the internal id (admin canonical). The modal's OK button carries the
 * in-flight state, so callers need no loading flag of their own.
 */
export const confirmAndSendInvoice = (
  ref: string,
  { modal, message, invoiceNumber, email }: ConfirmSendOptions
): void => {
  modal.confirm({
    title: 'Send this invoice to the patient?',
    content: email
      ? `${invoiceNumber || 'The invoice'} will be emailed to ${email}.`
      : `${
          invoiceNumber || 'The invoice'
        } will be emailed to the address on file.`,
    okText: 'Send',
    cancelText: 'Cancel',
    onOk: () => sendAndToast(ref, message)
  });
};
